import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { musicCreditsAPI } from '@/lib/api';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { Music, Users, Send, Plus, Minus, RefreshCw, ArrowRight, CreditCard } from 'lucide-react';
import PendingTransferAlert from '@/components/PendingTransferAlert';
import { toast } from 'sonner';

const MusicCreditsTab = () => {
  const queryClient = useQueryClient();
  const [allocateStudent, setAllocateStudent] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState(1);
  const [topupAmount, setTopupAmount] = useState(10);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferAmount, setTransferAmount] = useState(1);
  const [showTransfer, setShowTransfer] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['guardian-music-credits'],
    queryFn: async () => (await musicCreditsAPI.getGuardianCredits()).data,
  });

  const allocateMut = useMutation({
    mutationFn: (d) => musicCreditsAPI.allocate(d),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['guardian-music-credits'] });
      toast.success(res.data.message);
      setAllocateStudent(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Allocation failed'),
  });

  const autoSplitMut = useMutation({
    mutationFn: () => musicCreditsAPI.autoSplit(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['guardian-music-credits'] });
      toast.success(res.data.message);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Split failed'),
  });

  const topupMut = useMutation({
    mutationFn: (d) => musicCreditsAPI.topup(d),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['guardian-music-credits'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      toast.success(res.data.message);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Top-up failed'),
  });

  const transferMut = useMutation({
    mutationFn: (d) => musicCreditsAPI.transfer(d),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['guardian-music-credits'] });
      toast.success(res.data.message);
      setShowTransfer(false);
      setTransferEmail('');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Transfer failed'),
  });

  if (isLoading) return (
    <div className="text-center py-16">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3" />
      <p className="font-bold text-gray-500">Loading music credits...</p>
    </div>
  );

  const { total_granted = 0, unallocated = 0, total_allocated = 0, total_used = 0, children = [] } = data || {};

  return (
    <div className="space-y-6" data-testid="music-credits-tab">
      {/* Pending Transfer Alerts */}
      <PendingTransferAlert />

      {/* Overview Card */}
      <BrutalCard shadow="xl" className="bg-gradient-to-r from-violet-50 to-indigo-50">
        <div className="flex items-center gap-3 mb-4">
          <Music size={28} className="text-indigo-600" />
          <div>
            <h2 className="text-2xl font-black uppercase">Music Credits</h2>
            <p className="text-gray-600 font-medium text-sm">Manage song download credits for your children</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-xl text-center border-2 border-indigo-100">
            <p className="text-2xl font-black text-indigo-600">{total_granted}</p>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Total Granted</p>
          </div>
          <div className="bg-white p-3 rounded-xl text-center border-2 border-amber-100">
            <p className="text-2xl font-black text-amber-600">{unallocated}</p>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Unallocated</p>
          </div>
          <div className="bg-white p-3 rounded-xl text-center border-2 border-emerald-100">
            <p className="text-2xl font-black text-emerald-600">{total_allocated}</p>
            <p className="text-[10px] font-bold text-gray-500 uppercase">With Children</p>
          </div>
          <div className="bg-white p-3 rounded-xl text-center border-2 border-rose-100">
            <p className="text-2xl font-black text-rose-500">{total_used}</p>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Used</p>
          </div>
        </div>
      </BrutalCard>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {unallocated > 0 && children.length > 1 && (
          <BrutalButton variant="indigo" size="sm" onClick={() => autoSplitMut.mutate()}
            disabled={autoSplitMut.isPending}>
            <RefreshCw size={14} className="mr-1" /> Auto-Split to All Children
          </BrutalButton>
        )}
        <BrutalButton variant="emerald" size="sm" onClick={() => setShowTransfer(!showTransfer)}>
          <Send size={14} className="mr-1" /> Transfer to Member
        </BrutalButton>
      </div>

      {/* Transfer Section */}
      {showTransfer && (
        <BrutalCard shadow="md">
          <h3 className="font-black text-sm uppercase mb-3 flex items-center gap-2">
            <Send size={16} className="text-indigo-600" /> Transfer Credits
          </h3>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-3">
            <p className="text-xs font-bold text-amber-800">
              Transferred credits may not come back unless the recipient returns them to you. This action is like sending money — please confirm before sending.
            </p>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs font-bold text-gray-500 block mb-1">Email or Student Code</label>
              <input type="text" value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)}
                placeholder="email@example.com or 6-digit code"
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 text-sm font-bold" />
            </div>
            <div className="w-24">
              <label className="text-xs font-bold text-gray-500 block mb-1">Credits</label>
              <input type="number" min="1" max={unallocated} value={transferAmount}
                onChange={(e) => setTransferAmount(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 text-sm font-bold text-center" />
            </div>
            <BrutalButton variant="indigo" size="sm"
              onClick={() => {
                if (!window.confirm(`Send ${transferAmount} credits to "${transferEmail}"? They must accept with their PIN. Credits are held until accepted.`)) return;
                const isCode = /^\d{4,8}$/.test(transferEmail.trim());
                transferMut.mutate(isCode
                  ? { to_student_code: transferEmail.trim(), credits: transferAmount }
                  : { to_email: transferEmail.trim(), credits: transferAmount }
                );
              }}
              disabled={transferMut.isPending || !transferEmail || transferAmount < 1}>
              <ArrowRight size={14} className="mr-1" /> Send
            </BrutalButton>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            The recipient will see a pending transfer alert and must enter their PIN to accept. If declined, credits are returned to you.
          </p>
        </BrutalCard>
      )}

      {/* Children Credit Balances */}
      <div>
        <h3 className="text-lg font-black uppercase mb-3 flex items-center gap-2">
          <Users size={20} className="text-indigo-600" /> Children's Credits
        </h3>
        {children.length === 0 ? (
          <BrutalCard shadow="md" className="text-center py-8">
            <p className="text-gray-500 font-bold">No children found. Add students first.</p>
          </BrutalCard>
        ) : (
          <div className="space-y-3">
            {children.map(child => (
              <BrutalCard key={child.student_id} shadow="md" data-testid={`child-credits-${child.student_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Music size={18} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">{child.student_name}</p>
                      <p className="text-xs text-gray-500">
                        <span className="font-black text-indigo-600">{child.music_credits}</span> song credits available
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {allocateStudent === child.student_id ? (
                      <div className="flex items-center gap-1">
                        <input type="number" min="1" max={Math.max(unallocated, child.music_credits)}
                          value={allocateAmount} onChange={(e) => setAllocateAmount(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 px-2 py-1 rounded-lg border-2 border-gray-200 text-sm font-bold text-center" />
                        {unallocated > 0 && (
                          <button onClick={() => allocateMut.mutate({ student_id: child.student_id, credits: allocateAmount })}
                            className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-all"
                            title="Give credits">
                            <Plus size={14} />
                          </button>
                        )}
                        {child.music_credits > 0 && (
                          <button onClick={() => allocateMut.mutate({ student_id: child.student_id, credits: -allocateAmount })}
                            className="p-1.5 rounded-lg bg-rose-100 text-rose-700 hover:bg-rose-200 transition-all"
                            title="Reclaim credits">
                            <Minus size={14} />
                          </button>
                        )}
                        <button onClick={() => setAllocateStudent(null)}
                          className="text-xs font-bold text-gray-400 ml-1">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setAllocateStudent(child.student_id); setAllocateAmount(1); }}
                        className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-bold hover:bg-indigo-100 transition-all">
                        Manage
                      </button>
                    )}
                  </div>
                </div>
              </BrutalCard>
            ))}
          </div>
        )}
      </div>

      {/* Top Up Section */}
      <BrutalCard shadow="lg" className="bg-gradient-to-r from-emerald-50 to-teal-50">
        <h3 className="font-black text-sm uppercase mb-3 flex items-center gap-2">
          <CreditCard size={16} className="text-emerald-600" /> Buy More Credits
        </h3>
        <p className="text-xs text-gray-500 mb-3">Purchase additional song credits using your wallet balance. Credits are added to your unallocated pool.</p>
        <div className="flex gap-2 flex-wrap">
          {[10, 25, 50, 100].map(amt => (
            <button key={amt} onClick={() => setTopupAmount(amt)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${topupAmount === amt ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 border-2 border-emerald-200'}`}>
              {amt} songs
            </button>
          ))}
        </div>
        <div className="mt-3">
          <BrutalButton variant="emerald" size="sm"
            onClick={() => { if (window.confirm(`Buy ${topupAmount} music credits from your wallet?`)) topupMut.mutate({ credits: topupAmount }); }}
            disabled={topupMut.isPending}>
            <Plus size={14} className="mr-1" /> Buy {topupAmount} Credits
          </BrutalButton>
        </div>
      </BrutalCard>
    </div>
  );
};

export default MusicCreditsTab;
